import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ThemeProvider } from './theme/ThemeProvider';
import RisksSectionTabs from './components/RisksSectionTabs';
import StickyMainPanels from './components/StickyMainPanels';
import {
  buildSectionSearchParams,
  rememberDashboardSearch,
  rememberSection,
  rememberStatusSearch,
  resolveSectionFromSearchParams,
} from './pages/risksTabState';
import './styles/risks.css';

/**
 * Risks dashboard embedded in Mira Business (no separate risk login).
 * Org list stays full, same as risk.mira.cloud.
 */
const RisksContent = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const section = useMemo(
    () => resolveSectionFromSearchParams(searchParams),
    [searchParams],
  );
  const [hydrated, setHydrated] = useState(false);

  // Normalize URL once: every section has matching `tab=` (incl. dashboard).
  useEffect(() => {
    const currentSearch = searchParams.toString() ? `?${searchParams.toString()}` : '';
    if (searchParams.get('tab') !== section) {
      setSearchParams(buildSectionSearchParams(section, { currentSearch }), { replace: true });
    }
    rememberSection(section);
    setHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hydrated) {
      return;
    }
    rememberSection(section);
  }, [hydrated, section]);

  useEffect(() => {
    if (!hydrated) {
      return;
    }
    const query = searchParams.toString();
    const search = query ? `?${query}` : '';
    if (section === 'status') {
      rememberStatusSearch(search);
    } else if (section === 'dashboard') {
      rememberDashboardSearch(search);
    }
  }, [hydrated, section, searchParams]);

  const handleTabChange = useCallback(
    (nextSection) => {
      if (!nextSection || nextSection === section) {
        return;
      }

      const currentQuery = searchParams.toString();
      const currentSearch = currentQuery ? `?${currentQuery}` : '';
      if (section === 'status') {
        rememberStatusSearch(currentSearch);
      } else if (section === 'dashboard') {
        rememberDashboardSearch(currentSearch);
      }

      rememberSection(nextSection);
      setSearchParams(buildSectionSearchParams(nextSection, { currentSearch }), { replace: true });
    },
    [section, searchParams, setSearchParams],
  );

  const page = <StickyMainPanels activeTab={section} onTabChange={handleTabChange} />;

  return (
    <ThemeProvider>
      <div className="risks-root w-full bg-slate-50 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] text-slate-900 md:p-6">
        {/* One tab strip for all sections — avoids jump when pages remount TopBar. */}
        <div className="mb-3 w-full min-w-0 shrink-0">
          <RisksSectionTabs activeTab={section} onChange={handleTabChange} />
        </div>
        {page}
      </div>
    </ThemeProvider>
  );
};

export default RisksContent;
